import { useRef, useEffect, useState, useCallback } from "react";

/**
 * Custom hook to read the height of simplebar-content element at any given time
 * @param {Object} options - Configuration options
 * @param {boolean} options.observe - Whether to continuously observe height changes (default: true)
 * @param {number} options.debounceMs - Debounce time for height updates in milliseconds (default: 100)
 * @returns {Object} - Object containing height value and ref to attach to Scrollbar component
 */
export const useSimplebarHeight = ({
  observe = true,
  debounceMs = 100,
} = {}) => {
  const scrollbarRef = useRef(null);
  const [height, setHeight] = useState(0);
  const timeoutRef = useRef(null);

  // Function to get the current height
  const getHeight = useCallback(() => {
    if (!scrollbarRef.current) return 0;

    // Find the simplebar-content element within the scrollbar
    const simplebarContent =
      scrollbarRef.current.querySelector(".simplebar-content");
    if (!simplebarContent) return 0;

    return simplebarContent.offsetHeight;
  }, []);

  // Function to update height with debouncing
  const updateHeight = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const currentHeight = getHeight();
      setHeight(currentHeight);
    }, debounceMs);
  }, [getHeight, debounceMs]);

  // Effect to set up ResizeObserver for continuous monitoring
  useEffect(() => {
    if (!observe || !scrollbarRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    // Observe the scrollbar container
    resizeObserver.observe(scrollbarRef.current);

    // Also observe the simplebar-content if it exists
    const simplebarContent =
      scrollbarRef.current.querySelector(".simplebar-content");
    if (simplebarContent) {
      resizeObserver.observe(simplebarContent);
    }

    // Initial height measurement
    updateHeight();

    return () => {
      resizeObserver.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [observe, updateHeight]);

  // Manual height getter function
  const getCurrentHeight = useCallback(() => {
    const currentHeight = getHeight();
    setHeight(currentHeight);
    return currentHeight;
  }, [getHeight]);

  return {
    height,
    scrollbarRef,
    getCurrentHeight,
  };
};
